import * as jsonpatch from 'fast-json-patch';
import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { BoardId } from '@shared/models/boardId';
import { ResourceSet } from '@shared/models/events/resourceSet';
import { ResourceUpdated } from '@shared/models/events/resourceUpdated';
import { ResourceType } from '@shared/models/resourceType';
import { BoardEvent } from '@shared/models/boardEvent';
import { ResourceName } from '@shared/models/resourceName';

export interface ResourceState<TResource> {
  resource?: TResource;
  errorStartTime?: number;
}

export interface ResourceStates<TResource> {
  [boardId: BoardId]: ResourceState<TResource>;
}

function getOrCreateResourceState<TResource>(
  boardStates: ResourceStates<TResource>,
  boardId: BoardId,
): ResourceState<TResource> {
  let boardState = boardStates[boardId];
  if (!boardState) {
    boardState = boardStates[boardId] = {};
  }
  return boardState;
}

export function createResourceSlice<TResource extends ResourceType>(
  resourceName: ResourceName,
) {
  const initialState: ResourceStates<TResource> = {};

  return createSlice({
    name: resourceName,
    initialState,
    reducers: {
      resourceSet(state, action: PayloadAction<ResourceSet<TResource>>) {
        const event = action.payload;
        const resourceState = getOrCreateResourceState(state, event.board);
        resourceState.resource = event.set as TResource extends infer V
          ? V extends object
            ? Draft<TResource>
            : V
          : never;
        resourceState.errorStartTime = event.errorStart;
      },
      resourceUpdated(state, action: PayloadAction<ResourceUpdated>) {
        const event = action.payload;
        const resourceState = getOrCreateResourceState(state, event.board);
        if (!resourceState.resource) {
          return;
        }
        jsonpatch.applyPatch(resourceState.resource, event.updates);
      },
      resourceError(state, action: PayloadAction<BoardEvent>) {
        const event = action.payload;
        const resourceState = getOrCreateResourceState(state, event.board);
        resourceState.errorStartTime = Date.now();
      },
      resourceErrorResolved(state, action: PayloadAction<BoardEvent>) {
        const event = action.payload;
        const resourceState = getOrCreateResourceState(state, event.board);
        resourceState.errorStartTime = undefined;
      },
    },
  });
}
